/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { create } from 'zustand';

export interface ApiRequestEntry {
  id: string;
  timestamp: number;
  duration: number | null;
  method: string;
  url: string;
  status: number | null;
  requestHeaders: Record<string, string>;
  requestBody: any;
  responseHeaders: Record<string, string> | null;
  responseBody: any;
  error: string | null;
}

interface ApiInterceptorState {
  entries: ApiRequestEntry[];
  maxEntries: number;
  onRequestStart: (entry: Omit<ApiRequestEntry, 'duration' | 'status' | 'responseHeaders' | 'responseBody' | 'error'>) => void;
  onRequestEnd: (id: string, result: { status: number; responseHeaders: Record<string, string>; responseBody: any }) => void;
  onRequestError: (id: string, error: string) => void;
  clear: () => void;
  getById: (id: string) => ApiRequestEntry | undefined;
}

let idCounter = 0;
/**
 * Generates a unique request identifier for the API inspector.
 */
export function generateRequestId(): string {
  return `req_${Date.now()}_${++idCounter}`;
}

export const useApiInterceptorStore = create<ApiInterceptorState>((set, get) => ({
  entries: [],
  maxEntries: 500,

  onRequestStart: (partial) => {
    const entry: ApiRequestEntry = {
      ...partial,
      duration: null,
      status: null,
      responseHeaders: null,
      responseBody: undefined,
      error: null,
    };

    set((state) => {
      const next = [entry, ...state.entries];
      if (next.length > state.maxEntries) next.length = state.maxEntries;
      return { entries: next };
    });
  },

  onRequestEnd: (id, result) => {
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id
          ? {
              ...e,
              duration: Date.now() - e.timestamp,
              status: result.status,
              responseHeaders: result.responseHeaders,
              responseBody: result.responseBody,
            }
          : e,
      ),
    }));
  },

  onRequestError: (id, error) => {
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id
          ? { ...e, duration: Date.now() - e.timestamp, error }
          : e,
      ),
    }));
  },

  clear: () => set({ entries: [] }),

  getById: (id) => get().entries.find((e) => e.id === id),
}));
